import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, ButtonBase, InputBase } from '@mui/material';

// 강한 ease-out. 기본 CSS ease-out보다 초반 반응이 빨라 UI가 즉각 반응하는 느낌을 준다.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const modalPop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const rowEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================
// Modal Container
// ============================================
export const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
});

export const ModalContainer = styled(Box)({
  // 소속 회원 표의 상품 정보(상태칩+상품명+기간)가 한 줄에 들어가야 해서 920 → 1120 으로 넓혔다.
  width: '1120px',
  maxWidth: 'calc(100vw - 48px)',
  height: '640px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  // 모달은 트리거에 앵커되지 않으므로 중앙 origin 유지
  animation: `${modalPop} 180ms ${EASE_OUT}`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#262626',
  flexShrink: 0,
});

export const ModalTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#fff',
});

export const CloseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#fff',
  transition: 'opacity 150ms ease',
  '&:hover': {
    opacity: 0.8,
  },
});

export const ModalBody = styled(Box)({
  display: 'flex',
  flex: 1,
  minHeight: 0,
});

// ============================================
// 왼쪽 그룹 목록 패널
// ============================================
export const GroupPanel = styled(Box)({
  // 수정·삭제 버튼을 행 위에 겹치게 바꿔 이름이 쓸 수 있는 폭이 넓어졌다. 그만큼 패널을 줄인다.
  width: '225px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fafafa',
  borderRight: '1px solid #eee',
});

export const GroupPanelHeader = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  padding: '18px 20px 10px',
});

export const GroupPanelTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

export const GroupPanelCount = styled(Typography)({
  fontSize: '12px',
  color: '#999',
});

export const GroupScrollArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '0 12px 12px',
});

export const GroupRow = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'enterIndex',
})(({ isSelected, enterIndex }) => ({
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  padding: '10px 12px',
  marginBottom: '4px',
  borderRadius: '8px',
  textAlign: 'left',
  backgroundColor: isSelected ? '#fff' : 'transparent',
  boxShadow: isSelected ? '0 1px 4px rgba(0, 0, 0, 0.08), inset 0 0 0 1px #4AB3BC' : 'none',
  transition: `background-color 150ms ease, box-shadow 150ms ease, transform 160ms ${EASE_OUT}`,
  animation: `${rowEnter} 200ms ${EASE_OUT} both`,
  animationDelay: `${Math.min(enterIndex ?? 0, 8) * 30}ms`,
  '&:hover': {
    backgroundColor: isSelected ? '#fff' : '#f0f0f0',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  // 행에 호버했을 때만 수정/삭제 아이콘 노출
  '&:hover .group-row-actions': {
    opacity: 1,
    pointerEvents: 'auto',
  },
  // 아이콘이 회원수 자리를 대신한다. 평소엔 회원수가 보이고, 호버 때만 아이콘으로 바뀐다.
  // 예전처럼 아이콘을 왼쪽으로 밀면 좁은 행에서는 그게 곧 행 중앙이라, 그룹을 고르려고
  // 누른 클릭이 이름 편집으로 새어 나갔다.
  '&:hover .group-row-count': {
    opacity: 0,
  },
}));

export const GroupRowName = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '14px',
  fontWeight: isSelected ? 600 : 500,
  color: '#262626',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const GroupRowCount = styled(Typography)({
  fontSize: '12.5px',
  color: '#0F7F85',
  fontWeight: 600,
  flexShrink: 0,
  transition: 'opacity 150ms ease',
});

// 평소엔 이름이 폭을 다 쓰도록 자리를 비워 두고, 호버 때만 행 오른쪽 위에 겹쳐 띄운다.
export const GroupRowActions = styled(Box)({
  position: 'absolute',
  top: '50%',
  // 오른쪽 끝에 붙인다. 회원수는 호버 때 숨으므로 가릴 것이 없다.
  right: '8px',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '2px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.96)',
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.14)',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 150ms ease',
  zIndex: 2,
});

export const GroupRowIconButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  borderRadius: '7px',
  color: '#4AB3BC',
  cursor: 'pointer',
  transition: 'background-color 150ms ease, color 150ms ease',
  '&:hover': {
    backgroundColor: 'rgba(74, 179, 188, 0.14)',
    color: '#0F7F85',
  },
  '&.danger': {
    color: '#E57373',
  },
  '&.danger:hover': {
    backgroundColor: '#FDECEA',
    color: '#C2403D',
  },
});

// "+ 새 그룹" 버튼 — 점선 보더로 생성 액션임을 구분
export const NewGroupButton = styled(ButtonBase)({
  width: '100%',
  height: '38px',
  borderRadius: '8px',
  border: '1px dashed #c9c9c9',
  color: '#666',
  fontSize: '13px',
  fontWeight: 500,
  gap: '4px',
  transition: `border-color 150ms ease, color 150ms ease, background-color 150ms ease, transform 160ms ${EASE_OUT}`,
  '&:hover': {
    borderColor: '#4AB3BC',
    color: '#0F7F85',
    backgroundColor: 'rgba(74, 179, 188, 0.06)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
});

export const InlineInputWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 4px 4px 12px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  boxShadow: 'inset 0 0 0 1.5px #4AB3BC',
  marginBottom: '4px',
});

export const InlineInput = styled(InputBase)({
  flex: 1,
  fontSize: '14px',
  color: '#262626',
  '& input::placeholder': {
    color: '#bbb',
    opacity: 1,
  },
});

export const InlineInputAction = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  borderRadius: '6px',
  cursor: 'pointer',
  color: '#999',
  transition: 'background-color 150ms ease, color 150ms ease',
  '&:hover': {
    backgroundColor: '#f0f0f0',
    color: '#262626',
  },
  '&.confirm': {
    color: '#0F7F85',
  },
  '&.confirm:hover': {
    backgroundColor: 'rgba(74, 179, 188, 0.12)',
  },
});

// ============================================
// 오른쪽 회원 패널
// ============================================
export const MemberPanel = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const MemberPanelHeader = styled(Box)({
  padding: '18px 24px 0',
  flexShrink: 0,
});

export const MemberPanelTitleRow = styled(Box)({
  display: 'flex',
  // 그룹명·회원수 텍스트와 검색창의 세로 중심을 맞춘다(baseline 이면 입력창이 위로 떠 보인다).
  alignItems: 'center',
  gap: '8px',
  // 세로 리듬은 8px 배수로 맞춘다. 값을 하나씩 깎으면 리듬이 사라져 빽빽해진다.
  marginBottom: '10px',
});

export const MemberPanelTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const MemberPanelCount = styled(Typography)({
  fontSize: '16px',
  color: '#0F7F85',
  fontWeight: 700,
  flexShrink: 0,
});

// ============================================
// 회원 목록
// ============================================
export const MemberScrollArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '14px 24px 20px',
});

export const MemberRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'enterIndex',
})(({ enterIndex }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '9px 12px',
  borderRadius: '8px',
  transition: 'background-color 150ms ease',
  animation: `${rowEnter} 200ms ${EASE_OUT} both`,
  animationDelay: `${Math.min(enterIndex ?? 0, 10) * 25}ms`,
  '&:hover': {
    backgroundColor: '#fafafa',
  },
  '&:hover .member-row-remove': {
    opacity: 1,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const MemberRowName = styled(Typography)({
  width: '120px',
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const MemberRowPhone = styled(Typography)({
  width: '130px',
  flexShrink: 0,
  fontSize: '13px',
  color: '#666',
});

export const MemberRowDate = styled(Typography)({
  flex: 1,
  fontSize: '12px',
  color: '#bbb',
  textAlign: 'right',
});

// 제외 버튼. 행에 호버해야 보이면 있는 줄 모르고 지나쳐서 항상 노출한다.
export const MemberRowRemove = styled(Box)({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  borderRadius: '6px',
  // 회색이면 무슨 버튼인지 안 읽힌다. 평상시에도 붉은 기를 주되 배경은 호버에서만 깐다.
  color: '#E08A86',
  cursor: 'pointer',
  transition: 'background-color 150ms ease, color 150ms ease',
  '&:hover': {
    backgroundColor: '#FDECEA',
    color: '#C2403D',
  },
});

// ============================================
// 오른쪽 패널 탭 (회원 / 출결)
// ============================================
export const PanelTabs = styled(Box)({
  display: 'inline-flex',
  gap: '2px',
  padding: '3px',
  borderRadius: '9px',
  backgroundColor: '#f0f0f0',
  marginBottom: '12px',
});

export const PanelTab = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '28px',
  padding: '0 14px',
  borderRadius: '7px',
  fontSize: '13px',
  fontWeight: isActive ? 600 : 500,
  color: isActive ? '#0F7F85' : '#888',
  backgroundColor: isActive ? '#fff' : 'transparent',
  boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
  transition: 'background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',
  '&:hover': {
    color: isActive ? '#0F7F85' : '#555',
  },
}));

// ============================================
// 출결 탭
// ============================================
export const PeriodChip = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '30px',
  padding: '0 10px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: isActive ? 700 : 500,
  color: isActive ? '#0F7F85' : '#4B5563',
  border: `1px solid ${isActive ? '#0F7F85' : '#E4E8EB'}`,
  backgroundColor: isActive ? 'rgba(15,127,133,0.07)' : '#fff',
  transition: `background-color 150ms ease, color 150ms ease, transform 160ms ${EASE_OUT}`,
  '&:hover': {
    borderColor: '#0F7F85',
    backgroundColor: isActive ? 'rgba(15,127,133,0.12)' : '#F7FBFB',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
}));


export const SummaryChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: '6px',
  ...(tone === 'attended' && { color: '#0F7F85', backgroundColor: 'rgba(15, 127, 133, 0.08)' }),
  ...(tone === 'absent' && { color: '#F44336', backgroundColor: '#FDECEA' }),
  ...(tone === 'reserved' && { color: '#666', backgroundColor: '#f5f5f5' }),
}));

// 일자 · 시간 · 수업 · 회원 · 담당 강사 · 상태 · 입장 · 입·퇴실 — 결과를 회원 옆에 붙이고 입장 세부는 뒤로 묶는다
const ATTENDANCE_GRID = '96px 80px 1fr 76px 72px 96px 92px 96px';

// 회원별(MRow)과 같은 머리글 — 두 표가 한 화면에서 번갈아 뜨므로 결이 달라선 안 된다.
export const AttendanceHeaderRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: ATTENDANCE_GRID,
  gap: '12px',
  alignItems: 'center',
  padding: '10px 18px',
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#6B7280',
  textAlign: 'center',
  borderBottom: '1px solid #E8ECEF',
  position: 'sticky',
  top: 0,
  background: '#F6F8F9',
  zIndex: 1,
});

export const AttendanceRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'enterIndex' && prop !== 'isDateStart',
})(({ enterIndex, isDateStart }) => ({
  display: 'grid',
  gridTemplateColumns: ATTENDANCE_GRID,
  gap: '12px',
  alignItems: 'center',
  padding: '13px 18px',
  borderBottom: '1px solid #EFF2F4',
  fontSize: '13px',
  color: '#262626',
  textAlign: 'center',
  transition: 'background-color 150ms ease',
  animation: `${rowEnter} 200ms ${EASE_OUT} both`,
  animationDelay: `${Math.min(enterIndex ?? 0, 12) * 20}ms`,
  '&:hover': {
    backgroundColor: '#F8FAFB',
  },
  ...(isDateStart && { borderTop: '1px solid #E6EAEC' }),
  '& .cell-date, & .cell-time': {
    fontSize: '12px',
    color: '#666',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .cell-class': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  // 가운데 정렬이라 넘치면 양쪽이 잘린다. 유형 배지가 사라지지 않도록 이름만 줄인다.
  '& .cell-class > *:last-child': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .cell-trainer': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#6B7280',
  },
  '& .cell-name': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 500,
  },
  '& .cell-status': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    gap: '4px',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

/*
 * 유형 표시. 한 행에 유형·상태·입장 배지가 셋이나 겹쳐 알록달록해지므로
 * 유형은 배경 없이 글자만 두어 가장 조용한 층으로 내린다.
 */
export const AttendanceTypeBadge = styled('span')({
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 600,
  color: '#A2ABB2',
  letterSpacing: '0.02em',
});

export const AttendanceStatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '10px',
  '&::before': {
    content: '""',
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
  ...(status === 'ATTENDED' && { color: '#0F7F85', backgroundColor: 'rgba(15, 127, 133, 0.08)' }),
  ...(status === 'ABSENT' && { color: '#F44336', backgroundColor: '#FDECEA' }),
  // 예약 파랑은 참석관리·개인레슨 모달과 같은 관례 색이다.
  ...(status === 'RESERVED' && { color: '#007FFF', backgroundColor: 'rgba(0, 127, 255, 0.08)' }),
}));

// ============================================
// 빈 상태
// ============================================
export const EmptyState = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  color: '#bbb',
  padding: '24px',
});

export const EmptyTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#999',
});

export const EmptyDescription = styled(Typography)({
  fontSize: '13px',
  color: '#bbb',
  textAlign: 'center',
  lineHeight: 1.6,
});

// ============================================
// 회원 편집 (그룹 소속 회원 편집 모달 연결)
// ============================================

// 그룹 관리 모달 오른쪽 헤더에서 편집 모달을 여는 버튼
export const EditMembersButton = styled(ButtonBase)({
  flexShrink: 0,
  height: '38px',
  padding: '0 14px',
  borderRadius: '8px',
  border: '1px solid #4AB3BC',
  backgroundColor: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 600,
  gap: '5px',
  transition: `background-color 150ms ease, transform 160ms ${EASE_OUT}`,
  '&:hover': {
    backgroundColor: '#F0FAFB',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
  '&.Mui-disabled': {
    borderColor: '#DDD',
    color: '#BBB',
  },
});

// 편집 모달 오른쪽 목록에서 이번 편집으로 새로 담긴 회원 표시
export const NewMemberChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: '18px',
  padding: '0 6px',
  borderRadius: '9px',
  backgroundColor: '#E4F5F6',
  color: '#0F7F85',
  fontSize: '10.5px',
  fontWeight: 700,
});

// ============================================
// 그룹 추가 팝오버 (회원목록 플로팅 바)
// ============================================

export const PopoverPanel = styled(Box)({
  width: '272px',
  padding: '12px 0 8px',
  display: 'flex',
  flexDirection: 'column',
});

export const PopoverTitle = styled(Typography)({
  padding: '0 16px 8px',
  fontSize: '13px',
  fontWeight: 700,
  color: '#333',
});

export const PopoverScrollArea = styled(Box)({
  maxHeight: '260px',
  overflowY: 'auto',
  padding: '0 6px',
});

export const PopoverGroupRow = styled(ButtonBase)({
  width: '100%',
  justifyContent: 'flex-start',
  gap: '8px',
  height: '38px',
  padding: '0 10px',
  borderRadius: '7px',
  fontSize: '13px',
  color: '#333',
  '&:hover': {
    backgroundColor: '#F5F7F8',
  },
});

export const PopoverGroupName = styled('span')({
  flex: 1,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const PopoverGroupCount = styled('span')({
  fontSize: '11.5px',
  color: '#9AA3A9',
});

export const PopoverDivider = styled(Box)({
  height: '1px',
  margin: '6px 12px',
  backgroundColor: '#EEE',
});

export const PopoverNewGroupButton = styled(ButtonBase)({
  width: 'calc(100% - 12px)',
  margin: '0 6px',
  justifyContent: 'flex-start',
  gap: '5px',
  height: '36px',
  padding: '0 10px',
  borderRadius: '7px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#4AB3BC',
  '&:hover': {
    backgroundColor: '#F0FAFB',
  },
});

export const PopoverFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '8px 12px 0',
});

export const PopoverCancelButton = styled(ButtonBase)({
  height: '32px',
  padding: '0 12px',
  borderRadius: '6px',
  fontSize: '13px',
  color: '#777',
  '&:hover': { backgroundColor: '#F5F7F8' },
});

export const PopoverSubmitButton = styled(ButtonBase)({
  height: '32px',
  padding: '0 14px',
  borderRadius: '6px',
  backgroundColor: '#4AB3BC',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#3fa2ab' },
  '&.Mui-disabled': {
    backgroundColor: '#CCC',
    color: '#fff',
  },
});

export const PopoverEmptyText = styled(Typography)({
  padding: '18px 16px',
  fontSize: '12.5px',
  color: '#AAA',
  textAlign: 'center',
  lineHeight: 1.6,
});

// ============================================
// 회원 상세 소속 그룹 칩 (회원정보 탭 프로필 섹션)
// 같은 섹션의 '보유 포인트' 행과 같은 문법 — 라벨 + 내용 + 액션.
// ============================================

export const GroupChipRow = styled(Box)({
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 14px',
  background: '#fff',
  border: '1px solid #E0E0E0',
  borderRadius: '6px',
});

export const GroupChipLabel = styled(Typography)({
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: '#4B5563',
});

export const GroupChipList = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px',
});

export const GroupChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  height: '24px',
  padding: '0 4px 0 10px',
  borderRadius: '12px',
  backgroundColor: '#E4F5F6',
  color: '#0F7F85',
  fontSize: '12.5px',
  fontWeight: 600,
  maxWidth: '100%',
});

export const GroupChipRemove = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '18px',
  height: '18px',
  borderRadius: '9px',
  cursor: 'pointer',
  color: '#5E9FA3',
  '&:hover': {
    backgroundColor: 'rgba(15, 127, 133, 0.14)',
    color: '#0F7F85',
  },
});

export const GroupChipAddButton = styled(ButtonBase)({
  height: '24px',
  padding: '0 10px 0 6px',
  borderRadius: '12px',
  border: '1px dashed #B9C0C4',
  color: '#6B7280',
  fontSize: '12.5px',
  fontWeight: 500,
  gap: '2px',
  transition: `border-color 150ms ease, color 150ms ease, background-color 150ms ease`,
  '&:hover': {
    borderColor: '#4AB3BC',
    color: '#0F7F85',
    backgroundColor: 'rgba(74, 179, 188, 0.06)',
  },
});

export const GroupChipEmptyText = styled(Typography)({
  fontSize: '12.5px',
  color: '#AAA',
});

// ============================================
// 소속 회원 표 (회원 구분 · 이름 · 상품 정보)
// ============================================

export const MemberTable = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const MemberTableHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px',
  borderBottom: '1px solid #ECEFF1',
  backgroundColor: '#FAFAFA',
  position: 'sticky',
  top: 0,
  zIndex: 1,
});

// 정렬 가능한 헤더 셀. 활성 컬럼은 색으로 표시하고 화살표로 방향을 알린다.
export const MemberTableHeadCell = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'w' && prop !== 'grow' && prop !== 'isActive',
})(({ w, grow, isActive }) => ({
  width: grow ? 'auto' : w,
  flex: grow ? 1 : `0 0 ${w}`,
  minWidth: 0,
  justifyContent: 'center',
  gap: '2px',
  fontSize: '12px',
  fontWeight: 600,
  color: isActive ? '#0F7F85' : '#8A939C',
  borderRadius: '4px',
  padding: '2px 0',
  '&:hover': {
    color: '#0F7F85',
  },
}));

export const MemberTableRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'enterIndex',
})(({ enterIndex }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  borderBottom: '1px solid #F4F6F7',
  cursor: 'pointer',
  transition: 'background-color 150ms ease',
  animation: `${rowEnter} 200ms ${EASE_OUT} both`,
  animationDelay: `${Math.min(enterIndex ?? 0, 8) * 25}ms`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  '&:hover': {
    backgroundColor: '#F5FAFB',
  },
  '&:hover .member-row-remove': {
    opacity: 1,
  },
}));

export const MemberTableCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'w' && prop !== 'grow',
})(({ w, grow }) => ({
  width: grow ? 'auto' : w,
  flex: grow ? 1 : `0 0 ${w}`,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13px',
  color: '#262626',
}));

// 이름만 회원 상세로 가는 링크다. 나머지 영역은 체크 토글이라 여기만 링크로 보이게 한다.
export const MemberTableName = styled(MemberTableCell)({
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
  textAlign: 'center',
  lineHeight: '20px',
  cursor: 'pointer',
  transition: 'color 150ms ease',
  '&:hover': {
    color: '#0F7F85',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
});

export const SortArrow = styled('span')({
  fontSize: '9px',
  lineHeight: 1,
});

// ============================================
// 소속 회원 표 — 검색 / 구분 필터
// ============================================

export const MemberFilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  padding: '0 0 10px',
});

export const MemberSearchField = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: '0 0 240px',
  height: '32px',
  padding: '0 10px',
  borderRadius: '7px',
  border: '1px solid #DDE2E5',
  backgroundColor: '#fff',
  '&:focus-within': {
    borderColor: '#4AB3BC',
  },
  '& input': {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    color: '#262626',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: '#B9C0C4',
    },
  },
});

export const MemberFilterChip = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '26px',
  padding: '0 10px',
  borderRadius: '13px',
  border: `1px solid ${isActive ? '#4AB3BC' : '#E0E4E6'}`,
  backgroundColor: isActive ? '#E4F5F6' : '#fff',
  color: isActive ? '#0F7F85' : '#6B7280',
  fontSize: '12px',
  fontWeight: isActive ? 700 : 500,
  '&:hover': {
    borderColor: '#4AB3BC',
    color: '#0F7F85',
  },
  // 권한이 없거나 대상이 없을 때. 눌리지 않는다는 걸 색으로도 알린다.
  '&.Mui-disabled': {
    opacity: 0.45,
    color: '#9AA3A9',
    borderColor: '#E4E8EB',
  },
}));

export const MemberFilterCount = styled(Typography)({
  marginLeft: 'auto',
  fontSize: '12px',
  color: '#8A939C',
  whiteSpace: 'nowrap',
});

// ============================================
// 회원 탭 조건 패널 (회원목록 상세필터와 같은 조건, 모달용 레이아웃)
// ============================================

export const FilterIconButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '32px',
  padding: '0 10px',
  borderRadius: '7px',
  gap: '4px',
  border: `1px solid ${isActive ? '#4AB3BC' : '#DDE2E5'}`,
  backgroundColor: isActive ? '#E4F5F6' : '#fff',
  color: isActive ? '#0F7F85' : '#6B7280',
  fontSize: '12.5px',
  fontWeight: isActive ? 700 : 500,
  '&:hover': {
    borderColor: '#4AB3BC',
    color: '#0F7F85',
  },
}));

export const ConditionPanel = styled(Box)({
  border: '1px solid #DDE2E5',
  borderRadius: '8px',
  backgroundColor: '#FAFBFC',
  padding: '12px 14px',
  marginBottom: '10px',
});

export const ConditionGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: '18px',
  rowGap: '10px',
});

export const ConditionBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  minWidth: 0,
});

export const ConditionLabel = styled(Typography)({
  fontSize: '11.5px',
  fontWeight: 700,
  color: '#8A939C',
});

export const ConditionChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '5px',
});

export const ConditionDateRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const ConditionDateInput = styled('input')({
  height: '28px',
  padding: '0 8px',
  borderRadius: '6px',
  border: '1px solid #DDE2E5',
  fontSize: '12px',
  color: '#262626',
  backgroundColor: '#fff',
  outline: 'none',
  minWidth: 0,
  flex: 1,
  '&:focus': { borderColor: '#4AB3BC' },
});

export const ConditionNumberInput = styled('input')({
  width: '70px',
  height: '26px',
  padding: '0 8px',
  borderRadius: '13px',
  border: '1px solid #DDE2E5',
  fontSize: '12px',
  color: '#262626',
  outline: 'none',
  '&:focus': { borderColor: '#4AB3BC' },
});

export const ConditionFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '12px',
});

export const ConditionResetButton = styled(ButtonBase)({
  height: '30px',
  padding: '0 12px',
  borderRadius: '6px',
  fontSize: '12.5px',
  color: '#6B7280',
  border: '1px solid #DDE2E5',
  backgroundColor: '#fff',
  '&:hover': { borderColor: '#B9C0C4' },
});

export const ConditionApplyButton = styled(ButtonBase)({
  height: '30px',
  padding: '0 16px',
  borderRadius: '6px',
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#fff',
  backgroundColor: '#4AB3BC',
  '&:hover': { backgroundColor: '#3fa2ab' },
});

// ============================================
// 표 선택 액션바 / 이동·복사 팝오버
// ============================================

// 표 스크롤과 무관하게 패널 하단에 붙는 바. 스크롤 영역 밖에 렌더한다.
export const MemberActionBar = styled(Box)({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  backgroundColor: '#EAF6F7',
  borderTop: '1px solid #BFE4E7',
});

export const MemberActionCount = styled(Typography)({
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#0F7F85',
});

export const MemberActionButton = styled(ButtonBase)({
  height: '28px',
  padding: '0 10px',
  borderRadius: '6px',
  gap: '4px',
  border: '1px solid #4AB3BC',
  backgroundColor: '#fff',
  color: '#0F7F85',
  fontSize: '12.5px',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#F0FAFB' },
  '&.danger': {
    borderColor: '#F3C2BF',
    backgroundColor: '#FDF2F1',
    color: '#C2403D',
  },
  '&.danger:hover': { backgroundColor: '#FADFDD', borderColor: '#E4A19C' },
});

export const MemberActionClear = styled(ButtonBase)({
  marginLeft: 'auto',
  height: '28px',
  padding: '0 8px',
  borderRadius: '6px',
  fontSize: '12px',
  color: '#6B7280',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
});

export const ModeSegment = styled(Box)({
  display: 'flex',
  gap: '4px',
  margin: '0 12px 6px',
  padding: '3px',
  borderRadius: '8px',
  backgroundColor: '#F1F4F5',
});

export const ModeSegmentButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  height: '28px',
  borderRadius: '6px',
  fontSize: '12.5px',
  fontWeight: isActive ? 700 : 500,
  color: isActive ? '#0F7F85' : '#6B7280',
  backgroundColor: isActive ? '#fff' : 'transparent',
  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
}));

export const ModeHint = styled(Typography)({
  padding: '0 12px 8px',
  fontSize: '11.5px',
  color: '#8A939C',
});

// 날짜 조건이 한쪽만 채워졌을 때의 안내. 백엔드가 한쪽만 오면 조건을 아예 안 걸기 때문에 필요하다.
export const ConditionHint = styled(Typography)({
  fontSize: '11px',
  color: '#C46A00',
});

// ============================================
// 그룹 간 회원 이동 모달 (좌 기존 그룹 → 화살표 → 우 이동할 그룹)
// ============================================

export const TransferArrowColumn = styled(Box)({
  flexShrink: 0,
  width: '64px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
});

export const TransferArrowButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  border: `1px solid ${disabled ? '#DDE2E5' : '#4AB3BC'}`,
  backgroundColor: disabled ? '#F4F6F7' : '#fff',
  color: disabled ? '#C4CACE' : '#0F7F85',
  pointerEvents: disabled ? 'none' : 'auto',
  '&:hover': { backgroundColor: '#F0FAFB' },
}));

export const TransferSelect = styled(Box)({
  marginLeft: 'auto',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE2E5' },
});

export const TransferModeSegment = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginRight: '16px',
  '& label': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12.5px',
    color: '#4B5563',
    cursor: 'pointer',
  },
});

// 수업 셀을 눌러 해당 스케줄로 이동시키는 링크 표시
export const AttendanceClassLink = styled('span')({
  cursor: 'pointer',
  textDecorationLine: 'underline',
  textDecorationColor: 'transparent',
  transition: 'text-decoration-color 150ms ease, color 150ms ease',
  '&:hover': {
    color: '#0F7F85',
    textDecorationColor: '#0F7F85',
  },
});

// 입·퇴실 시각. 기록이 없으면 옅게 '-'.
export const AttendanceTimeCell = styled('span', {
  shouldForwardProp: prop => prop !== 'empty',
})(({ empty }) => ({
  fontSize: '12px',
  textAlign: 'center',
  color: empty ? '#C4CACE' : '#4B5563',
  fontVariantNumeric: 'tabular-nums',
}));

// 탭과 검색창을 같은 줄에 둔다 (검색은 회원 탭에서만 노출).
export const TabsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

// 이관 모달의 좌·우 패널. 옮겨 담은 게 있으면 받을 그룹에 테두리를 줘 어디로 가는지 보이게 한다.
export const TransferPanel = styled(Box, {
  shouldForwardProp: prop => prop !== 'isStaged',
})(({ isStaged }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: `1px solid ${isStaged ? '#4AB3BC' : '#E4E8EB'}`,
  boxShadow: isStaged ? '0 0 0 3px rgba(74, 179, 188, 0.16)' : 'none',
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  overflow: 'hidden',
}));

// 편집 모달의 소속 회원 제외(✕). 문자 수신자 편집과 공유하는 버튼이라 그쪽에 영향을 주지 않게
// 여기서 색만 덧씌운다. 평소엔 옅은 빨강, 올리면 진해진다.
export const RemoveMemberButtonSx = {
  color: '#E57373',
  '&:hover': { backgroundColor: '#FDECEA', color: '#C2403D' },
};

// 상품 정보 셀. 내용이 길면 가로로 넘치는데 스크롤바는 띄우지 않고 끌어서 보게 한다.
export const TicketScroller = styled(Box)({
  width: '100%',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  cursor: 'grab',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '&:active': { cursor: 'grabbing' },
});

// 이관 모달 하단의 복사 토글. 회색 라벨은 잘 안 읽혀 본문 색으로 올린다.
export const TransferCopyLabel = styled('label')({
  display: 'flex',
  alignItems: 'center',
  marginRight: '16px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#4B5563',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

// 담아둔 이동 예약을 한 번에 비운다.
export const TransferResetButton = styled(ButtonBase)({
  height: '38px',
  padding: '0 14px',
  borderRadius: '7px',
  border: '1px solid #DDE2E5',
  backgroundColor: '#fff',
  color: '#6B7280',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': { borderColor: '#B9C0C4', backgroundColor: '#F7F9FA' },
});

// 이관 모달의 그룹 선택 — 이름은 가운데 온전히 두고 옆 갈매기 버튼으로 메뉴를 연다.
export const TransferGroupPicker = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  height: '34px',
  padding: '0 10px',
  borderRadius: '9px',
  border: `1px solid ${isOpen ? '#4AB3BC' : 'transparent'}`,
  backgroundColor: isOpen ? '#F0FAFB' : 'transparent',
  transition: 'background-color 150ms ease, border-color 150ms ease',
  '&:hover': { backgroundColor: '#F2F5F6' },
}));

export const TransferGroupName = styled('span')({
  maxWidth: 'calc(100% - 30px)',
  fontSize: '14px',
  fontWeight: 700,
  color: '#262626',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TransferGroupChevron = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  color: isOpen ? '#0F7F85' : '#6B7280',
  transform: isOpen ? 'rotate(180deg)' : 'none',
  transition: 'transform 180ms ease, color 150ms ease',
}));

export const MenuGroupName = styled('span', {
  shouldForwardProp: prop => prop !== 'isCurrent',
})(({ isCurrent }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: isCurrent ? 700 : 500,
  color: isCurrent ? '#0F7F85' : '#262626',
}));

export const MenuGroupCount = styled('span')({
  flexShrink: 0,
  fontSize: '11.5px',
  color: '#9AA3A9',
});

// ============================================
// 출결 요약 — 색만으로 뜻을 전하지 않도록 항상 수치·라벨을 함께 둔다.
// ============================================

/*
 * 요약 줄. 카드 테두리 + 회색 배경 + 표 헤더가 겹치면 상자 안에 상자가 되어 촌스럽다.
 * 바탕은 흰색으로 두고 아래 선 하나로만 표와 나눈다.
 */
export const AttendanceSummaryCard = styled(Box)({
  display: 'flex',
  // 출석률 칸에만 막대가 붙어 바닥 정렬을 하면 숫자 줄이 서로 어긋난다. 위로 맞춘다.
  alignItems: 'flex-start',
  gap: '18px',
  padding: '10px 4px 8px 2px',
  marginTop: 0,
  minHeight: '64px',
  borderBottom: '1px solid #EDF0F2',
});

// 지난 수업(출석 vs 결석) 비율 막대. 옆 칸에 출석/결석 수치가 있어 색 없이도 읽힌다.
export const AttendanceBar = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  height: '6px',
  borderRadius: '99px',
  overflow: 'hidden',
  backgroundColor: '#EDF0F2',
});

export const AttendanceBarSegment = styled(Box, {
  shouldForwardProp: prop => prop !== 'tone' && prop !== 'ratio',
})(({ tone, ratio }) => ({
  width: `${ratio}%`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11.5px',
  fontWeight: 700,
  color: '#fff',
  whiteSpace: 'nowrap',
  backgroundColor:
    tone === 'attended' ? '#0F7F85' : '#F26D63',
  transition: 'width 200ms ease',
}));

export const AttendanceViewTabs = styled(Box)({
  display: 'flex',
  gap: '2px',
  alignItems: 'center',
  // 탭이 눌리면 글자가 세로로 꺾인다. 탭은 줄어들지 않는다.
  flexShrink: 0,
});

// 회색 트랙 세그먼트는 요약 줄 위에 또 하나의 상자를 얹는다. 밑줄 탭이 조용하다.
export const AttendanceViewTab = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '30px',
  padding: '0 10px',
  whiteSpace: 'nowrap',
  borderRadius: '6px 6px 0 0',
  fontSize: '12.5px',
  fontWeight: isActive ? 700 : 500,
  color: isActive ? '#0F7F85' : '#98A2AA',
  backgroundColor: 'transparent',
  boxShadow: isActive ? 'inset 0 -2px 0 #0F7F85' : 'none',
  transition: 'color 150ms ease, box-shadow 150ms ease',
  '&:hover': { color: isActive ? '#0F7F85' : '#4B5563' },
}));

// 입장 경로 배지 — 키오스크 / 안면 / 기록 없음(CRM 수기)
export const EntrySourceBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => {
  const map = {
    KIOSK: { fg: '#0F7F85', bg: 'transparent' },
    FACE: { fg: '#3564D6', bg: 'transparent' },
    NONE: { fg: '#98A2AA', bg: 'transparent' },
  };
  const color = map[tone] || map.NONE;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    height: '20px',
    padding: 0,
    fontSize: '11px',
    fontWeight: 600,
    color: color.fg,
    backgroundColor: color.bg,
    whiteSpace: 'nowrap',
    '&::before': {
      content: '""',
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      flexShrink: 0,
    },
  };
});

// 회원별 요약 표
export const MemberStatRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '110px 64px 1fr 120px 64px 56px',
  gap: '10px',
  alignItems: 'center',
  padding: '9px 10px',
  borderBottom: '1px solid #F4F6F7',
  fontSize: '13px',
});

export const MemberStatName = styled('span')({
  fontWeight: 600,
  color: '#262626',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const MemberStatRate = styled('span', {
  shouldForwardProp: prop => prop !== 'low',
})(({ low }) => ({
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: low ? '#C2403D' : '#0F7F85',
}));

export const MemberStatCounts = styled('span')({
  fontSize: '12px',
  color: '#6B7280',
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
});

// 회원별 통계의 펼침 화살표. 열리면 아래를 향한다.
export const ExpandChevron = styled('span', {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  display: 'inline-block',
  marginRight: '6px',
  fontSize: '11px',
  color: '#9AA3A9',
  transform: isOpen ? 'rotate(90deg)' : 'none',
  transition: 'transform 150ms ease',
}));

// 회원별 통계의 수업별 세부 행. 들여쓰기와 옅은 배경으로 종속 관계를 드러낸다.
export const ClassStatRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '110px 64px 1fr 120px 64px 56px',
  gap: '10px',
  alignItems: 'center',
  padding: '7px 10px 7px 30px',
  borderBottom: '1px solid #F4F6F7',
  backgroundColor: '#FAFCFC',
  fontSize: '12.5px',
  '& .class-name': {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#4B5563',
  },
});

// 기간 선택 버튼. 지금 걸린 기간을 그대로 적어 팝오버를 열지 않아도 알 수 있게 한다.
export const PeriodButton = styled('button', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '32px',
  padding: '0 11px',
  borderRadius: '8px',
  // 지점 스케줄의 기간 조회 버튼과 같은 톤. 켜져 있으면 테두리를 굵혀 상태를 드러낸다.
  border: `${isActive ? '2px' : '1px'} solid ${isActive ? '#4AB3BC' : '#d9d9d9'}`,
  backgroundColor: isActive ? 'rgba(15,127,133,0.1)' : '#fff',
  color: isActive ? '#4AB3BC' : '#737373',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'border-color 150ms ease, background-color 150ms ease, color 150ms ease',
  '&:hover': { borderColor: '#4AB3BC', color: '#4AB3BC', backgroundColor: '#f5f5f5' },
  '& .label': { fontVariantNumeric: 'tabular-nums' },
}));






// ============================================
// 출결 요약 KPI — 질문 하나당 숫자 하나
// ============================================
export const AttendanceKpi = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '58px',
});

export const AttendanceKpiValue = styled(Typography)({
  fontSize: '25px',
  fontWeight: 700,
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  color: '#1F2937',
  fontVariantNumeric: 'tabular-nums',
});

export const AttendanceKpiLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  color: '#98A2AA',
  letterSpacing: '0.02em',
  marginTop: '3px',
  whiteSpace: 'nowrap',
});

export const AttendanceKpiHint = styled(Typography)({
  fontSize: '10.5px',
  fontWeight: 600,
  color: '#B26A00',
  marginTop: '2px',
});

export const AttendanceKpiDivider = styled(Box)({
  width: '1px',
  height: '34px',
  marginTop: '3px',
  backgroundColor: '#EDF0F2',
});

// 필터 선택지가 없을 때. 빈 칩 줄 대신 이유를 적는다.
export const ConditionEmpty = styled('span')({
  fontSize: '12px',
  color: '#B9C0C4',
  padding: '4px 2px',
});

// 방문(체크인 입장) 행 — 일자 · 회원 · 입장 상품 · 입실 · 퇴실 · 체류 · 입장
// 회원별(MRow)·내역과 같은 골격. 탭만 바꿔 오가는 표라 결이 달라선 안 된다.
export const VisitRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDateStart' && prop !== 'header',
})(({ isDateStart, header }) => ({
  display: 'grid',
  gridTemplateColumns: '104px 82px 1fr 72px 84px 88px 96px',
  gap: '12px',
  alignItems: 'center',
  padding: header ? '10px 18px' : '13px 18px',
  borderBottom: header ? '1px solid #E8ECEF' : '1px solid #EFF2F4',
  fontSize: '13px',
  color: '#262626',
  textAlign: 'center',
  ...(header && {
    background: '#F6F8F9',
    fontSize: '12.5px',
    fontWeight: 700,
    color: '#6B7280',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }),
  ...(isDateStart && { borderTop: '1px solid #E6EAEC' }),
  ...(!header && { '&:hover': { backgroundColor: '#F8FAFB' } }),
  '& .cell-date': {
    fontSize: '12px',
    color: '#666',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .cell-name': {
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .cell-ticket': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minWidth: 0,
  },
  // 가운데 정렬이라 넘치면 양쪽이 잘린다. 유형 배지가 사라지지 않도록 이름만 줄인다.
  '& .ticket-name': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#4B5563',
  },
}));

// 수업 예약과 묶인 입장임을 밝히는 작은 표식.
export const VisitLinkedMark = styled('span')({
  flexShrink: 0,
  fontSize: '10.5px',
  fontWeight: 700,
  color: '#0F7F85',
  backgroundColor: 'rgba(15, 127, 133, 0.08)',
  borderRadius: '9px',
  padding: '2px 7px',
});

// 빠른 선택 프리셋 줄. 툴바에서 가로로 흐르되 좁아지면 줄바꿈한다.
/*
 * 기간 프리셋 줄. 프리셋이 늘어도 줄바꿈해서 오른쪽 버튼(건수·필터·엑셀)을 아래로
 * 밀어내면 안 되므로, 한 줄을 유지하고 넘치는 만큼만 가로로 흐르게 둔다.
 */
export const PeriodChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '4px',
  flex: '1 1 auto',
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '& > *': { flexShrink: 0 },
});

// 그룹 담기 팝오버에서 '이미 이 그룹에 있음'을 알리는 표식.
export const PopoverJoinedMark = styled('span')({
  flexShrink: 0,
  marginLeft: '4px',
  fontSize: '10.5px',
  fontWeight: 700,
  color: '#0F7F85',
  backgroundColor: 'rgba(15, 127, 133, 0.08)',
  borderRadius: '9px',
  padding: '2px 6px',
});

/*
 * 출석부 격자 — 행은 회원, 열은 날짜(또는 월).
 * 이름 열과 머리글은 고정해서 가로로 밀어도 누구의 어느 날인지 잃지 않는다.
 */
const BOOK_NAME_WIDTH = 96;
export const BOOK_SUMMARY_WIDTH = 62;
// 칸 너비 — 주간은 수업 시간까지 적으므로 넓게, 나머지는 한 화면에 많이 담는다.
const BOOK_COL_WIDTH = { week: 96, day: 32, weekly: 52, month: 56 };
const BOOK_ROW_HEIGHT = { week: 46, day: 32, weekly: 32, month: 32 };
const BOOK_TONE = {
  visited: '#0F7F85',
  attended: '#0F7F85',
  // 수업 없이 이용권·안면으로 들른 날 (#92). 수업 방문과 색을 갈라 한눈에 나뉜다.
  free: '#7A5AF8',
  absent: '#C2403D',
  reserved: '#5A8DEE',
};

export const BookLegend = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '8px 2px 10px',
  fontSize: '12px',
  color: '#6B7280',
  flexWrap: 'wrap',
});

export const BookLegendItem = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
});

export const BookLegendUnit = styled('span')({
  marginLeft: 'auto',
  color: '#9AA3A9',
});

export const BookScroll = styled(Box)({
  overflow: 'auto',
  maxHeight: '420px',
  border: '1px solid #E6EAEC',
  borderRadius: '8px',
});

export const BookGrid = styled(Box, {
  shouldForwardProp: prop => prop !== 'columns' && prop !== 'unit',
})(({ columns, unit }) => ({
  display: 'grid',
  // 하루 단위는 minmax 유동 폭 — 한 달 31칸이 1600px 에서 절반만 보이던 것을 패널 폭에
  // 맞춰 줄인다(마크 9px·날짜 두 자리가 18px 안에 든다). 넓은 화면에선 1fr 로 펴진다.
  gridTemplateColumns: unit === 'day'
    ? `${BOOK_NAME_WIDTH}px repeat(${columns}, minmax(18px, 1fr)) ${BOOK_SUMMARY_WIDTH}px ${BOOK_SUMMARY_WIDTH}px`
    : `${BOOK_NAME_WIDTH}px repeat(${columns}, ${BOOK_COL_WIDTH[unit] || 32}px) ${BOOK_SUMMARY_WIDTH}px ${BOOK_SUMMARY_WIDTH}px`,
  width: unit === 'day' ? '100%' : 'max-content',
  minWidth: '100%',
  fontVariantNumeric: 'tabular-nums',
}));

const bookHeadBase = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#F7F9FA',
  borderBottom: '1px solid #E6EAEC',
  fontSize: '11px',
  color: '#6B7280',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '36px',
};

export const BookHeadName = styled('div')({
  ...bookHeadBase,
  left: 0,
  zIndex: 3,
  justifyContent: 'flex-start',
  padding: '0 10px',
  borderRight: '1px solid #E6EAEC',
});

export const BookHeadCell = styled('div', {
  shouldForwardProp: prop => prop !== 'isWeekend' && prop !== 'isToday',
})(({ isWeekend, isToday }) => ({
  ...bookHeadBase,
  flexDirection: 'column',
  gap: '1px',
  color: isToday ? '#0D9298' : isWeekend ? '#C2403D' : '#6B7280',
  ...(isToday && { fontWeight: 800, backgroundColor: '#EAF6F6' }),
  '& .label': { fontSize: '11px', lineHeight: 1 },
  '& .sub': { fontSize: '9px', lineHeight: 1, color: '#AEB6BB' },
}));

export const BookHeadSummary = styled('div', {
  shouldForwardProp: prop => prop !== 'offset',
})(({ offset }) => ({
  ...bookHeadBase,
  position: 'sticky',
  right: `${offset}px`,
  zIndex: 3,
  borderLeft: '1px solid #E6EAEC',
  fontSize: '10px',
}));

export const BookName = styled('div')({
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: '#fff',
  borderRight: '1px solid #E6EAEC',
  borderBottom: '1px solid #F4F6F7',
  padding: '0 10px',
  minHeight: '32px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '12.5px',
  fontWeight: 600,
  color: '#262626',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const BookCell = styled('div', {
  shouldForwardProp: prop => prop !== 'isWeekend' && prop !== 'unit' && prop !== 'isToday',
})(({ isWeekend, unit, isToday }) => ({
  height: `${BOOK_ROW_HEIGHT[unit] || 32}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  borderBottom: '1px solid #F4F6F7',
  // 오늘 열은 옅은 세로 띠로 위치를 잡아 준다. 주말 배경보다 우선한다.
  backgroundColor: isToday ? '#F2FAFA' : isWeekend ? '#FBFCFC' : 'transparent',
}));

export const BookMark = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  width: '9px',
  height: '9px',
  borderRadius: '50%',
  backgroundColor: tone === 'reserved' ? 'transparent' : BOOK_TONE[tone] || '#C9CFD3',
  border: tone === 'reserved' ? `1.5px solid ${BOOK_TONE.reserved}` : 'none',
  boxSizing: 'border-box',
  flexShrink: 0,
}));

export const BookCellCount = styled('span')({
  fontSize: '10px',
  color: '#6B7280',
});

export const BookMonthCount = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  fontSize: '12.5px',
  fontWeight: 700,
  color: BOOK_TONE[tone] || '#6B7280',
}));

export const BookSummaryCell = styled('div', {
  shouldForwardProp: prop => prop !== 'offset',
})(({ offset }) => ({
  position: 'sticky',
  right: `${offset}px`,
  zIndex: 1,
  backgroundColor: '#fff',
  minHeight: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderLeft: '1px solid #E6EAEC',
  borderBottom: '1px solid #F4F6F7',
  fontSize: '12px',
  color: '#6B7280',
}));

export const BookFootName = styled('div')({
  position: 'sticky',
  left: 0,
  bottom: 0,
  zIndex: 3,
  backgroundColor: '#F7F9FA',
  borderRight: '1px solid #E6EAEC',
  borderTop: '1px solid #E6EAEC',
  padding: '0 10px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6B7280',
});

export const BookFootCell = styled('div', {
  shouldForwardProp: prop => prop !== 'isWeekend' && prop !== 'unit',
})(({ isWeekend }) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  backgroundColor: isWeekend ? '#F2F5F6' : '#F7F9FA',
  borderTop: '1px solid #E6EAEC',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11.5px',
  fontWeight: 600,
  color: '#0F7F85',
}));

// 칸 툴팁 — 그 날의 수업을 줄바꿈 그대로 보여준다.
export const BookTip = styled('span')({
  display: 'block',
  whiteSpace: 'pre-line',
  fontSize: '11.5px',
  lineHeight: 1.5,
});

// 주간 상세 칸 — 그날 수업을 시간까지 세로로 쌓는다.
export const BookDetail = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px',
  width: '100%',
  padding: '0 6px',
});

export const BookDetailLine = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '11px',
  fontWeight: 600,
  color: BOOK_TONE[tone] || '#6B7280',
  whiteSpace: 'nowrap',
}));

// 합계 행의 요약 칸 — 아래·오른쪽 양쪽에 붙는 모서리다.
export const BookFootSummary = styled('div', {
  shouldForwardProp: prop => prop !== 'offset',
})(({ offset }) => ({
  position: 'sticky',
  bottom: 0,
  right: `${offset}px`,
  zIndex: 3,
  backgroundColor: '#F7F9FA',
  borderTop: '1px solid #E6EAEC',
  borderLeft: '1px solid #E6EAEC',
  minHeight: '32px',
}));

// 예약해 놓고 안 온 날. 점이 아니라 ✕ 라야 '비어 있음'과 구분된다.
export const BookAbsentMark = styled('span')({
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1,
  color: '#C2403D',
});

// 회원별 합계 칸의 숫자. 0은 흐리게 둬서 다녀간 회원이 먼저 보이게 한다.
export const BookSummaryValue = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  fontSize: '12.5px',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: tone === 'muted' ? '#C9CFD3' : BOOK_TONE[tone] || '#6B7280',
}));

/* 회원별 뷰 펼침의 요일별 집계 줄 (#92). 월~일 7칸 + 라벨. */
export const DayStatRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '7px 10px 9px 30px',
  borderBottom: '1px solid #F4F6F7',
  backgroundColor: '#FAFCFC',
  fontSize: '12px',
  color: '#555',
});

export const DayStatCell = styled('span', {
  shouldForwardProp: prop => prop !== 'muted',
})(({ muted }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '44px',
  padding: '3px 6px',
  borderRadius: '8px',
  background: muted ? 'transparent' : '#EFF6F6',
  color: muted ? '#B6BDC4' : '#2C3E50',
  '& .day': { fontSize: '11px', fontWeight: 700 },
  '& .val': { fontSize: '12px' },
}));

/* 지각 표시 (#92). 내역 뷰 상태 칩 옆과 회원별 지각 열이 같이 쓴다. */
export const LateBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 6px',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: 700,
  color: '#C46A00',
  background: 'rgba(196, 106, 0, 0.12)',
  whiteSpace: 'nowrap',
});

/* ===== 출결 통계 시각화 (#92) — 팔레트는 검증기 통과값이다 ===== */
/* 출석 #0D9298 · 결석 #C2403D · 예정 #5A8DEE (CVD ΔE 12.9, 대비 3:1 이상) */

export const StatRingWrap = styled(Box)({
  position: 'relative',
  width: '58px',
  height: '58px',
  flexShrink: 0,
});

export const StatRingCenter = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13.5px',
  fontWeight: 800,
  color: '#2C3E50',
});

/* 요일별 출석 미니 컬럼. 막대는 단일색(순차 규칙) — 요일 식별은 라벨이 맡는다. */
export const WeekdayChart = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '5px',
  height: '44px',
  padding: '0 2px',
});

export const WeekdayCol = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3px',
  width: '17px',
});

export const WeekdayBar = styled(Box, {
  shouldForwardProp: prop => prop !== 'ratio',
})(({ ratio }) => ({
  width: '12px',
  height: `${Math.max(ratio * 30, ratio > 0 ? 3 : 0)}px`,
  borderRadius: '3px 3px 0 0',
  backgroundColor: ratio > 0 ? '#0D9298' : 'transparent',
  // 0인 요일도 자리를 지켜야 패턴이 읽힌다. 바닥선만 남긴다.
  borderBottom: '2px solid #E3E8EA',
  boxSizing: 'content-box',
}));

export const WeekdayLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'weekend',
})(({ weekend }) => ({
  fontSize: '10px',
  fontWeight: 700,
  color: weekend ? '#C2403D' : '#98A1A8',
}));

/* KPI 숫자 옆 색 도트 — 색 단독 의존을 피한다(라벨과 함께). */
export const StatDot = styled('span', {
  shouldForwardProp: prop => prop !== 'color',
})(({ color }) => ({
  display: 'inline-block',
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: '5px',
  verticalAlign: 'middle',
}));

/* 출결 1단 모드 스위치 (#92 재구성) — 수업 출결(예약 기반) / 시설 이용(입장 기반). */
export const ModeSwitch = styled(Box)({
  display: 'inline-flex',
  padding: '3px',
  gap: '2px',
  background: '#EFF2F4',
  borderRadius: '9px',
  marginRight: '6px',
  flexShrink: 0,
});

export const ModeSwitchButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  height: '26px',
  padding: '0 12px',
  borderRadius: '7px',
  fontSize: '12.5px',
  fontWeight: isActive ? 700 : 500,
  whiteSpace: 'nowrap',
  color: isActive ? '#0D9298' : '#7A858D',
  backgroundColor: isActive ? '#fff' : 'transparent',
  boxShadow: isActive ? '0 1px 2px rgba(20,40,50,0.10)' : 'none',
}));

/* 이용 통계 행 (#92 재구성) — 회원별 방문·체류·주 이용 상품. */
export const UsageRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '120px 76px 76px 90px 90px 1fr 96px',
  gap: '10px',
  alignItems: 'center',
  padding: '9px 10px',
  borderBottom: '1px solid #F4F6F7',
  fontSize: '13px',
  fontVariantNumeric: 'tabular-nums',
});

/* 출결 탭 줄 (#92 재구성) — 모드(1단)와 서브뷰(2단)가 요약 위에 한 줄로 선다. */
export const AttendanceTabsBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '4px',
  borderBottom: '1px solid #E6EAEC',
});

/* 기간 프리셋 추가 버튼 (#93). 프리셋이 늘어도 툴바가 무너지지 않게 한다. */
export const PresetArrow = styled('button')({
  flexShrink: 0,
  width: '28px',
  height: '32px',
  borderRadius: '8px',
  border: '1px solid #d9d9d9',
  background: '#fff',
  color: '#737373',
  fontSize: '15px',
  lineHeight: 1,
  cursor: 'pointer',
  '&:hover': { borderColor: '#4AB3BC', color: '#4AB3BC', backgroundColor: '#f5f5f5' },
});

/* 프리셋 줄을 담는 창. 좌우 화살표가 이 안에서 칩 위에 겹쳐 뜬다. */
export const PresetViewport = styled(Box)({
  position: 'relative',
  flex: '1 1 auto',
  minWidth: 0,
  marginRight: '10px',
});

/*
 * 좌우 이동 화살표 (#93). 툴바에 자리를 차지하면 그만큼 프리셋이 덜 보이므로
 * 칩 위에 겹쳐 띄운다. 화살표 옆으로 흰 그라데이션을 깔아 '뒤에 더 있다'가 읽히게 한다.
 */
export const PresetEdgeArrow = styled('button', {
  shouldForwardProp: prop => prop !== 'edge',
})(({ edge }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [edge]: 0,
  zIndex: 2,
  width: '34px',
  padding: 0,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: edge === 'left' ? 'flex-start' : 'flex-end',
  color: '#8A939C',
  fontSize: '17px',
  lineHeight: 1,
  // 테두리·그림자 없이 칩이 흰색으로 사라지게만 한다. 잘린 게 아니라 이어진다는 표시다.
  background: edge === 'left'
    ? 'linear-gradient(to right, #fff 45%, rgba(255,255,255,0) 100%)'
    : 'linear-gradient(to left, #fff 45%, rgba(255,255,255,0) 100%)',
  '&:hover': { color: '#4AB3BC' },
}));

/*
 * 크게 보기 토글 (#92). 상태 드롭다운 바로 왼쪽에 서므로 높이·모서리를 맞춘다.
 * 켜져 있을 때는 테두리가 아니라 바탕을 채운다 — 지금 넓게 보는 중인지가 한눈에 보여야 한다.
 */
export const WideToggle = styled('button', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  height: '32px',
  padding: '0 13px',
  borderRadius: '8px',
  // 꺼져 있을 때도 민트 테두리를 둘러 옆의 회색 드롭다운과 구별되게 한다.
  border: `1.5px solid ${isActive ? '#0F7F85' : '#8FD9D3'}`,
  background: isActive ? '#0F7F85' : '#fff',
  color: isActive ? '#fff' : '#0F7F85',
  fontSize: '12.5px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  // 스킬 기준 — 150~300ms, ease-out. 눌렀다는 느낌만 주고 끝낸다.
  transition: `background 180ms ${EASE_OUT}, border-color 180ms ${EASE_OUT},
    color 180ms ${EASE_OUT}, transform 180ms ${EASE_OUT}, box-shadow 180ms ${EASE_OUT}`,
  '& .wide-icon': { transition: `transform 180ms ${EASE_OUT}` },
  '&:hover': {
    borderColor: '#0F7F85',
    background: isActive ? '#0C6B70' : '#F2FBFA',
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 10px rgba(15, 127, 133, 0.22)',
    '& .wide-icon': { transform: isActive ? 'scale(0.88)' : 'scale(1.14)' },
  },
  '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
  '&:focus-visible': { outline: '2px solid #14A79C', outlineOffset: '1px' },
  // 움직임을 끈 사용자에게는 색만 바뀐다.
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease',
    '&:hover': { transform: 'none', boxShadow: 'none', '& .wide-icon': { transform: 'none' } },
  },
}));

/*
 * 검색어를 치는 동안 바로 뜨는 결과 목록 (#78). 아래 표가 이미 걸러지긴 하지만
 * 목록이 길면 어디가 걸렸는지 눈으로 찾아야 한다 — 위에서 바로 집어 갈 수 있게 한다.
 */
export const SearchPreview = styled(Box)({
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  width: '100%',
  maxHeight: '260px',
  overflowY: 'auto',
  zIndex: 30,
  background: '#fff',
  border: '1px solid #E3E8EC',
  borderRadius: '10px',
  boxShadow: '0 12px 28px rgba(15,23,42,0.14)',
  padding: '4px',
});

export const SearchPreviewRow = styled(ButtonBase)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '8px 10px',
  borderRadius: '7px',
  fontSize: '13px',
  color: '#1F2937',
  '&:hover': { background: '#F2FBFA' },
});

export const SearchPreviewEmpty = styled(Box)({
  padding: '14px 10px',
  textAlign: 'center',
  fontSize: '12.5px',
  color: '#9CA3AF',
});

/* 프리셋 칩 + 삭제 표식. 버튼 안에 버튼을 넣지 않으려고 바깥에 겹쳐 둔다. */
export const PresetSlot = styled('span')({
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
  '&:hover > button:last-of-type': { opacity: 1 },
});

export const PresetRemove = styled('button')({
  position: 'absolute',
  top: '-5px',
  right: '-5px',
  width: '16px',
  height: '16px',
  padding: 0,
  borderRadius: '50%',
  border: '1px solid #DCE2E6',
  background: '#fff',
  color: '#6B7280',
  fontSize: '11px',
  lineHeight: 1,
  cursor: 'pointer',
  opacity: 0,
  transition: 'opacity 120ms ease',
  '&:hover': { background: '#FDEEEC', borderColor: '#F5C6C0', color: '#C2453A' },
  '&:focus-visible': { opacity: 1 },
});

export const PresetAmountInput = styled('input')({
  flex: 1,
  minWidth: 0,
  height: '32px',
  padding: '0 10px',
  borderRadius: '7px',
  border: '1px solid #DCE2E6',
  fontSize: '13px',
  color: '#1F2937',
  '&:focus-visible': { outline: '2px solid #14A79C', outlineOffset: '1px' },
});

/*
 * 머리글·라벨 옆에 붙는 물음표. 설명은 툴팁으로만 두고 자리는 거의 차지하지 않는다 —
 * 열 폭이 빠듯해서 글로 풀어 쓰면 표가 무너진다.
 */
export const HintMark = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '13px',
  height: '13px',
  marginLeft: '3px',
  borderRadius: '50%',
  border: '1px solid #C4CBD2',
  color: '#9AA3AC',
  fontSize: '9px',
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'help',
  flexShrink: 0,
  verticalAlign: 'middle',
  '&:hover': { borderColor: '#8A939C', color: '#6B7280' },
});

/*
 * 수업 유형 칩 (#92). 이름 앞에 서서 '무슨 수업인지'를 먼저 알린다 — 글자만 있던
 * AttendanceTypeBadge 는 이름에 묻혀 안 읽혔다. 색은 검증기 통과 팔레트에서 가져왔다.
 */
const CLASS_TYPE_TONE = {
  GROUP_LESSON: { color: '#0F8A80', bg: 'rgba(20, 167, 156, 0.12)' },
  PRIVATE_LESSON: { color: '#2E6FD0', bg: 'rgba(61, 130, 232, 0.12)' },
  DUET_LESSON: { color: '#7050C0', bg: 'rgba(138, 99, 210, 0.12)' },
};

export const ClassTypeChip = styled('span', {
  shouldForwardProp: prop => prop !== 'classType',
})(({ classType }) => {
  const tone = CLASS_TYPE_TONE[classType] || { color: '#6B7280', bg: '#EEF1F4' };
  return {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    color: tone.color,
    background: tone.bg,
  };
});

/*
 * 그룹수업(템플릿) 표식 (#92). 수업 유형 칩(그룹·개인·듀엣)과 생김새를 일부러 달리한다 —
 * 둘이 비슷하면 '그냥 수업'과 '그룹수업'이 또 구분되지 않는다. 이건 테두리가 있는 상자다.
 */
export const GroupClassChip = styled('span')({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: 800,
  whiteSpace: 'nowrap',
  color: '#0F7F85',
  background: '#fff',
  border: '1px solid #8FD9D3',
});

/* 템플릿에 안 묶인 수업을 모아 두는 줄 머리 (#92). */
export const ClassSectionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '9px 18px',
  background: '#F8FAFB',
  borderBottom: '1px solid #EFF2F4',
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#6B7280',
});

/*
 * 크게 보기 (#92). 모달 안 표가 좁아 컬럼이 눌리는 문제를 화면 전체로 펴서 푼다.
 * 열리면서 살짝 커지는 느낌만 주고 끝낸다 — 220ms, ease-out, 움직임을 끈 사용자는 즉시.
 */
const wideEnter = keyframes`
  from { transform: scale(0.97); opacity: 0.5; }
  to { transform: scale(1); opacity: 1; }
`;

export const WideOverlay = styled(Box)({
  position: 'fixed',
  inset: '18px',
  zIndex: 1400,
  background: '#fff',
  borderRadius: '14px',
  boxShadow: '0 24px 60px rgba(15,23,42,0.25)',
  padding: '14px 20px 18px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  animation: `${wideEnter} 220ms ${EASE_OUT} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/*
 * 요약과 표를 담는 자리. 평소엔 위아래로 쌓이고, 크게 보기에서는 표가 왼쪽을 다 쓰고
 * 요약·범례가 오른쪽 기둥으로 접힌다(row-reverse 라 JSX 순서는 그대로 둔다).
 */
export const AttendanceMain = styled(Box, {
  shouldForwardProp: prop => prop !== 'wide',
})(({ wide }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
  ...(wide && {
    flexDirection: 'row-reverse',
    alignItems: 'stretch',
    gap: '18px',
    // 오른쪽 기둥 — 요약 카드가 가로에서 세로로 접힌다.
    '& > .attendance-summary': {
      width: '250px',
      flexShrink: 0,
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '14px',
      borderBottom: 'none',
      borderLeft: '1px solid #EDF0F2',
      paddingLeft: '18px',
      minHeight: 0,
      overflowY: 'auto',
    },
    '& > .attendance-summary > *': { flexShrink: 0 },
    // 링과 제목은 가운데로 세우고, 수치는 '이름 왼쪽 · 값 오른쪽' 한 줄씩으로 눕힌다.
    '& .attendance-ring': { alignSelf: 'center' },
    '& .attendance-title': {
      marginTop: 0,
      textAlign: 'center',
      paddingBottom: '2px',
    },
    '& .attendance-kpi': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      minWidth: 0,
      marginTop: 0,
      padding: '6px 2px',
      borderBottom: '1px solid #F1F4F6',
      '& > *:first-of-type': { order: 2, fontSize: '17px' },
      '& > *:last-of-type': { order: 1, fontSize: '12.5px', marginTop: 0 },
    },
    '& .attendance-legend': {
      alignItems: 'flex-start',
      marginTop: 'auto',
      paddingTop: '10px',
      '& > div': { flexWrap: 'wrap' },
    },
    // 세로 기둥에서 세로 구분선은 보이지 않는다. 가로줄로 눕힌다.
    '& > .attendance-summary .attendance-divider': {
      width: '100%',
      height: '1px',
      alignSelf: 'stretch',
      margin: 0,
    },
    '& > .attendance-table': { flex: 1, minWidth: 0 },
  }),
}));

/* 수업별 표 열폭 (#92). 이름 칸에 유형 배지와 일정까지 들어가 회원별보다 넓다. */
export const CLASS_GRID = '1.7fr 72px 1.4fr 88px 64px 56px';

/* 표 바로 위 줄 — 왼쪽은 유형 칩, 오른쪽은 상태 드롭다운 (#92). */
export const TableTopRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '2px 0 6px',
});

export const StatusSelect = styled('select')({
  height: '32px',
  padding: '0 28px 0 12px',
  borderRadius: '8px',
  border: '1px solid #DCE2E6',
  background: '#fff',
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#4B5563',
  cursor: 'pointer',
  transition: 'border-color 150ms ease, color 150ms ease',
  // 네이티브 화살표가 OS 마다 달라 배경 삼각형으로 그린다.
  appearance: 'none',
  backgroundImage:
    'linear-gradient(45deg, transparent 50%, #8A939C 50%), linear-gradient(135deg, #8A939C 50%, transparent 50%)',
  backgroundPosition: 'calc(100% - 15px) 14px, calc(100% - 10px) 14px',
  backgroundSize: '5px 5px, 5px 5px',
  backgroundRepeat: 'no-repeat',
  '&:hover': { borderColor: '#C4CBD2' },
  '&:focus-visible': { outline: '2px solid #14A79C', outlineOffset: '1px' },
});

/* 회원별 출결 행 (#92 시안) — 회원 | 출석률 | 3색 스택바 | 출석/결석 | 지각 | 예정. */
export const MRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'header',
})(({ header }) => ({
  display: 'grid',
  gridTemplateColumns: '1.3fr 72px 1.6fr 88px 64px 56px',
  gap: '12px',
  alignItems: 'center',
  padding: header ? '10px 18px' : '13px 18px',
  borderBottom: header ? '1px solid #E8ECEF' : '1px solid #EFF2F4',
  ...(header && {
    background: '#F6F8F9',
    fontSize: '12.5px',
    fontWeight: 700,
    color: '#6B7280',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }),
}));
